// Admin LTR isolation (Rev 7.11 hotfix — 2026-02-XX)
//
// The public site relies on a global `crewznation_lang` toggle in
// `LanguageProvider` that sets `<html dir="rtl" lang="ar">` when the
// visitor picks Arabic. The admin portal (AdminLogin / AdminDashboard)
// is an English-only internal tool — it must NEVER inherit that RTL
// flip on its navigation, sidebar, tables, action columns, or filter
// controls.
//
// This hook forces `<html dir="ltr" lang="en">` on mount and installs
// a MutationObserver so any subsequent flip by the public language
// toggle (e.g. an open second tab that changes state) is reverted
// while the admin surface is mounted. On unmount the hook restores
// the direction/lang from the persisted `crewznation_lang` preference
// so the public site keeps its normal behaviour when the operator
// navigates away.
import { useEffect } from "react";
import { LANG_KEY } from "../lib/i18n";

export function useAdminLtrLock() {
  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    const html = document.documentElement;
    const prevDir = html.getAttribute("dir");
    const prevLang = html.getAttribute("lang");

    const applyLtr = () => {
      if (html.getAttribute("dir") !== "ltr") html.setAttribute("dir", "ltr");
      if (html.getAttribute("lang") !== "en") html.setAttribute("lang", "en");
    };
    applyLtr();

    let scheduled = false;
    const observer = new MutationObserver(() => {
      // Guard against infinite loops: only re-apply if drift detected
      if (scheduled) return;
      if (
        html.getAttribute("dir") === "ltr" &&
        html.getAttribute("lang") === "en"
      ) {
        return;
      }
      scheduled = true;
      // microtask so we don't fight LanguageProvider's own setter in the same tick
      Promise.resolve().then(() => {
        applyLtr();
        scheduled = false;
      });
    });
    observer.observe(html, { attributes: true, attributeFilter: ["dir", "lang"] });

    return () => {
      observer.disconnect();
      // Restore from the persisted public preference (if any) so the
      // public site direction stays correct when the operator leaves.
      let restoreLang = prevLang || "en";
      try {
        const stored = typeof localStorage !== "undefined"
          ? localStorage.getItem(LANG_KEY)
          : null;
        if (stored === "ar" || stored === "en") restoreLang = stored;
      } catch (_) { /* ignore */ }
      html.setAttribute("dir", restoreLang === "ar" ? "rtl" : "ltr");
      html.setAttribute("lang", restoreLang);
      // If we never had a prior dir attribute at all, best-effort keep the restored one
      void prevDir;
    };
  }, []);
}

export default useAdminLtrLock;
